import { Trans } from "@web-monorepo/i18n/macro";
import { DDSButton } from "@web-monorepo/dds";
import ShadowCard from "src/shared/components/ShadowCard";
import FullScreenCardPage from "./FullScreenCardPage";
import SlideNavigation from "./SlideNavigation";
import type { RemoteSlideProps, Slide } from "./types";
import { resolveQuestion, resolveText, type SlideQuestion, type SlideText } from "./text";
import WithTeacherTips from "./WithTeacherTips";
import styles from "./OptionsSlide.module.css";

type Option = {
  id: string;
  option: SlideText;
  icon?: string;
  twist?: { twist: SlideText; showTwist: boolean };
  description?: SlideText;
};

export const OptionsSlide = (
  question: SlideQuestion,
  options: Option[],
  twists?: Option[],
  theSentenceStarter?: SlideText,
): Slide => {
  return {
    remote: ({ next, prev }: RemoteSlideProps) => (
      <WithTeacherTips>
        <div className={styles.frame}>
          <QuestionWithOptionsRemote question={question} options={options} twists={twists} next={next} prev={prev} />
          <SlideNavigation
            onBack={() => next(-1)}
            onNext={() => {
              if (twists && twists.length > 0) {
                next(2);
              } else {
                next(getNextCount(options));
              }
            }}
          />
        </div>
      </WithTeacherTips>
    ),
    display: () => (
      <QuestionWithOptionsDisplay question={question} sentenceStarter={theSentenceStarter} options={options} />
    ),
  };
};

function getNextCount(options: Option[]): number {
  let count = 1;
  let twistCount = 0;
  for (const option of options) {
    if (option.twist) {
      if (!option.twist.showTwist) {
        count += Math.pow(2, twistCount);
      }
      twistCount++;
    }
  }
  return count;
}

type QuestionWithOptionsRemoteProps = {
  question: SlideQuestion;
  options: Option[];
  twists?: Option[];
  next: (value?: number) => void;
  prev: (value?: number) => void;
};

function QuestionWithOptionsRemote({ question, options, twists, next, prev }: QuestionWithOptionsRemoteProps) {
  return (
    <>
      <ShadowCard className={styles.card}>
        <div className={styles.question}>{resolveQuestion(question)}</div>
        {options.map((option, index) => (
          <div key={option.id} className={styles.optionRow}>
            {option.icon && <img src={option.icon} alt="" className={styles.optionIcon} />}
            <div className={styles.optionText}>
              <OptionTextRemote
                option={option}
                next={next.bind(null, Math.pow(2, index))}
                prev={prev.bind(null, Math.pow(2, index))}
              />
            </div>
          </div>
        ))}
      </ShadowCard>

      {twists && twists.length > 0 && (
        <div>
          <h3 className={styles.twistHeading}>
            <Trans>Add twist (optional)</Trans>
          </h3>
          {twists.map(({ id, option, icon, description }) => (
            <ShadowCard key={id} className={styles.twistCard} onClick={() => next()}>
              {icon && <img src={icon} alt="" className={styles.twistIcon} />}
              <div className={styles.twistTextWrapper}>
                <div className={styles.twistOption}>{resolveText(option)}</div>
                <div>{description && resolveText(description)}</div>
              </div>
              <div className={styles.twistAddPlus}>{"＋"}</div>
            </ShadowCard>
          ))}
        </div>
      )}
    </>
  );
}

type OptionTextRemoteProps = {
  option: Option;
  next: (num?: number) => void;
  prev: (num?: number) => void;
};

function OptionTextRemote({ option, next, prev }: OptionTextRemoteProps) {
  const { option: label, description, twist } = option;
  const twistText = twist ? resolveText(twist.twist) : "";
  const onToggle = () => {
    if (twist && twist.showTwist) {
      prev();
    } else {
      next();
    }
  };
  return (
    <div>
      {resolveText(label)}
      {description && <div>{resolveText(description)}</div>}

      {twist &&
        (twist.showTwist ? (
          <>
            <DDSButton
              data-name="options_slide:hide_twist"
              variant="tertiary"
              size="small"
              className={styles.twistToggle}
              onClick={onToggle}
            >
              <Trans>Hide Twist</Trans>
            </DDSButton>
            <div>
              <Trans>
                <div className={styles.twistLabel}>Twist:</div>
                <div>{twistText}</div>
              </Trans>
            </div>
          </>
        ) : (
          <>
            <DDSButton
              data-name="options_slide:show_twist"
              variant="tertiary"
              size="small"
              className={styles.twistToggle}
              onClick={onToggle}
            >
              <Trans>Show Twist</Trans>
            </DDSButton>
            <div>
              <Trans>
                <div className={styles.twistLabel}>Hidden twist:</div>
                <div className={styles.hiddenTwistText}>{twistText}</div>
              </Trans>
            </div>
          </>
        ))}
    </div>
  );
}

type QuestionWithOptionsDisplayProps = {
  question: SlideQuestion;
  options: Option[];
  sentenceStarter?: SlideText;
};

function QuestionWithOptionsDisplay({ question, options, sentenceStarter }: QuestionWithOptionsDisplayProps) {
  return (
    <FullScreenCardPage>
      <div className={styles.displayQuestion}>{resolveQuestion(question)}</div>
      {sentenceStarter && <div className={styles.displaySentenceStarter}>{resolveText(sentenceStarter)}</div>}
      <div className={styles.displayCardRow}>
        {options.map(({ id, option, icon, twist, description }) => (
          <ShadowCard key={id} className={styles.displayCard}>
            <div className={styles.displayCardInner}>
              {icon && <img src={icon} alt="" className={styles.displayIcon} />}
              <div>
                <div className={styles.displayOption}>{resolveText(option)}</div>
                {description && <div className={styles.displayDescription}>{resolveText(description)}</div>}
                {twist && twist.showTwist && <div className={styles.displayTwist}>{resolveText(twist.twist)}</div>}
              </div>
            </div>
          </ShadowCard>
        ))}
      </div>
    </FullScreenCardPage>
  );
}
